import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useParams, useLocation } from "react-router-dom";
import { companyUpdatesColumns } from "./companyUpdates.columns";
import {
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
} from "@mui/x-data-grid-pro";

const CompanyUpdatesDataGrid = forwardRef(({ searchText }, ref) => {
  const { companyId } = useParams();
  const location = useLocation();
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract preserved state for API call
  const dynamicPartner = location.state?.partner_doc;
  const dynamicKpi = location.state?.kpi || "CARR";
  const dynamicDealId = location.state?.deal_id;

  /* ---------------- FORMATTING UTILITY ---------------- */
  /**
   * Safely converts backend values to strings with 2 decimal points.
   * Handles commas in strings (e.g. "1,240.50") and null/NA values.
   */
  /* const formatToTwoDecimals = (val) => {
    if (val === null || val === undefined || val === "NA" || val === "") return "—";
    
    // Remove commas to allow parseFloat to work correctly on large numbers
    const cleanStr = String(val).replace(/,/g, "");
    const num = parseFloat(cleanStr);
    
    return isNaN(num) ? "—" : num.toFixed(2);
  }; */

  /* ---------------- FORMATTING UTILITY ---------------- */
  const formatToTwoDecimals = (val) => {
    // 1. Handle actual null/undefined/empty cases
    if (val === null || val === undefined || val === "NA" || val === "") return "—";

    // 2. Clean the string (remove commas) to check if it's numeric
    const cleanStr = String(val).replace(/,/g, "");
    const num = parseFloat(cleanStr);

    // 3. IF it is NOT a number (e.g., "CFBE", "CF+"), return the string as is
    if (isNaN(num)) return val;

    // 4. IF it IS a number, fix to 2 decimals
    return num.toFixed(2);
  };

  useEffect(() => {
    const fetchBackendData = async () => {
      // Ensure we have the necessary identifiers to fetch data
      if (!companyId && !dynamicDealId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';

        // Using dealId as the primary key for updates as per latest backend structure
        const url = `${API_BASE_URL}/portfolio/updates?dealId=${dynamicDealId}&partnerCode=${dynamicPartner}&company=${location.state?.company_name}&kpi=${dynamicKpi}`;
        const response = await fetch(url);
        const result = await response.json();

        if (result.status === "success" && result.data) {
          const transformedRows = result.data.map((item, index) => {
            // Apply 2-decimal formatting to all numeric metrics
            const carrVal = formatToTwoDecimals(item.Top_Line_KPI_Value);
            const annualVal = formatToTwoDecimals(item.Annual_KPI_Value);
            const cashVal = formatToTwoDecimals(item.Net_Cash_Balance);
            const burnVal = formatToTwoDecimals(item.Monthly_Burn);

            return {
              id: index, // Unique ID for DataGrid
              date: item.Date || "—",
              rank: item.Rank || "—",
              grade: item.Grade || "NA",
              reportingCurrency: item.Reporting_Currency || "—",
              // Concatenate currency symbols only if numeric data exists
              carr: carrVal !== "—" ? `${carrVal}M` : "—",
              annualKpi: annualVal !== "—" ? `${annualVal}M` : "—",
              //netCashBalance: cashVal !== "—" ? `${cashVal}M` : "—",
              netCashBalance: (cashVal !== "—" && !isNaN(parseFloat(cashVal)))
                ? `${cashVal}M`
                : cashVal,
              monthlyBurn: (burnVal !== "—" && !isNaN(parseFloat(burnVal)))
                ? `${burnVal}M`
                : burnVal,
              updates: item.News || "No updates available",
              //cashOut: item.Cash_Out_Date || "—",
              cashOut: item.Cash_Out_Date
                ? new Date(item.Cash_Out_Date).toISOString().split("T")[0]
                : "—",
            };
          });
          setRows(transformedRows);
        }
      } catch (error) {
        console.error("Error fetching updates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBackendData();
  }, [companyId, dynamicDealId, dynamicPartner, dynamicKpi]);

  /* ---------------- ENTERPRISE SEARCH ---------------- */
  const filteredRows = useMemo(() => {
    if (!searchText) return rows;
    const q = searchText.toLowerCase().trim();
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, searchText]);

  /* ---------------- EXCEL EXPORT (VISIBLE ONLY) ---------------- */
  const exportToExcel = () => {
    if (!apiRef.current) return;

    const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
    if (!rowIds.length) return;

    const visibleFields = gridVisibleColumnFieldsSelector(apiRef);

    const exportData = rowIds.map((id) => {
      const row = apiRef.current.getRow(id);
      const obj = {};
      visibleFields.forEach((field) => {
        // Map field keys to human-readable headers from your column definitions
        const column = companyUpdatesColumns.find(col => col.field === field);
        const headerName = column ? column.headerName : field;
        obj[headerName] = row[field];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Company Updates");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `Updates_${companyId}_${new Date().toLocaleDateString()}.xlsx`);
  };

  /* ---------------- EXPOSE METHODS TO PARENT ---------------- */
  useImperativeHandle(ref, () => ({
    openColumnsPanel: () => apiRef.current?.showPreferences("columns"),
    openFiltersPanel: () => apiRef.current?.showPreferences("filters"),
    exportToExcel,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={500}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 500, width: "100%", bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
      <DataGridPro
        apiRef={apiRef}
        rows={filteredRows}
        columns={companyUpdatesColumns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "date", sort: "desc" }],
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb"
          },
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            lineHeight: "1.5 !important",
            py: 1,
            display: "flex",
            alignItems: "start"
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#fcfcfc"
          }
        }}
        getRowHeight={() => 'auto'} // Responsive row height for long news updates
      />
    </Box>
  );
});

export default CompanyUpdatesDataGrid;